// RAPIFAST :: Verificación administrativa de negocios aliados (DEMO con datos ficticios).
// Secciones A–H del Formulario de Registro y Verificación de Negocios Aliados v1.0.
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Ban, Eye } from "lucide-react";
import { PageHeader } from "@/components/kit";
import { DemoBanner, Badge, SemaforoBadge, Field, SectionCard, ConfidentialDocModal } from "@/components/verificationKit";
import { useVerificationDemo } from "@/context/VerificationDemoContext";
import {
  VERIF_STATUS, BUSINESS_ACCEPTANCES, BUSINESS_CHECKS, businessSemaforo,
} from "@/lib/verificationMock";

const inputCls =
  "rounded-lg border border-[#333] bg-[#1F1F1F] px-2.5 py-1.5 text-xs text-gray-200 outline-none focus:border-[#FF6A00]";

function BusinessDetail({ b, onPatch, onClose }) {
  const [notes, setNotes] = useState(b.reviewNotes || "");
  const [viewSupport, setViewSupport] = useState(null);
  const checksDone = b.checks.filter(Boolean).length;
  const allOk = checksDone === b.checks.length;

  const toggleCheck = (i) => {
    const next = [...b.checks];
    next[i] = !next[i];
    onPatch(b.id, { checks: next });
  };
  const approve = () => {
    if (!allOk) {
      toast.error(`No se puede aprobar: faltan ${b.checks.length - checksDone} verificaciones internas`);
      return;
    }
    onPatch(b.id, { status: "APPROVED", reviewNotes: notes, reviewedBy: "Admin demo", reviewedAt: new Date().toISOString().slice(0, 10) });
    toast.success("Negocio aprobado y vinculado (demo)");
  };
  const reject = () => {
    if (!notes.trim()) {
      toast.error("El motivo de rechazo es obligatorio");
      return;
    }
    onPatch(b.id, { status: "REJECTED", reviewNotes: notes, reviewedBy: "Admin demo", reviewedAt: new Date().toISOString().slice(0, 10) });
    toast.success("Negocio rechazado con motivo (demo)");
  };

  return (
    <div className="mt-3 space-y-4" data-testid={`business-detail-${b.id}`}>
      <SectionCard title="A. Datos del negocio" testId="business-section-a">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Nombre comercial" value={b.commercialName} />
          <Field label="Razón social / titular" value={b.legalName} />
          <Field label="NIT / identificación" value={b.nit} sensitive testId="business-nit" />
          <Field label="Tipo de actividad" value={b.activityType} />
          <Field label="Dirección" value={b.address} />
          <Field label="Municipio / departamento" value={b.municipality} />
          <Field label="Teléfono" value={b.phone} />
          <Field label="Correo" value={b.email} />
          <Field label="Horario" value={b.schedule} />
        </div>
      </SectionCard>

      <SectionCard title="B. Persona responsable y autorizados" testId="business-section-b">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Responsable" value={b.responsible.name} />
          <Field label="Documento del responsable" value={b.responsible.doc} sensitive />
          <Field label="Celular del responsable" value={b.responsible.phone} />
          <Field label="Autorizados para crear solicitudes / entregar paquetes" value={b.authorizedDispatchers} />
        </div>
      </SectionCard>

      <SectionCard title="C. Información operativa" testId="business-section-c">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Dirección de recogida (si difiere)" value={b.ops.pickupAddress} />
          <Field label="Referencias de recogida" value={b.ops.references} />
          <Field label="Tipos de productos" value={b.ops.productTypes} />
          <Field label="Tamaño / peso habitual" value={b.ops.dimensionsWeight} />
          <Field label="Condiciones especiales" value={b.ops.specialHandling} />
          <Field label="Volumen estimado" value={b.ops.volume} />
          <Field label="Quién entrega al conductor" value={b.ops.dispatcher} />
        </div>
      </SectionCard>

      <SectionCard title="D. Aceptación de tarifas y reglas" testId="business-section-d">
        <ul className="space-y-2">
          {BUSINESS_ACCEPTANCES.map((txt, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
              <span className={`mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full ${b.acceptances[i] ? "bg-emerald-400" : "bg-gray-600"}`} />
              {txt}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          Modelo económico propuesto del piloto: 75% conductor / 25% RAPIFAST (provisional).
        </p>
      </SectionCard>

      <SectionCard title="E. Método de pago y facturación" testId="business-section-e">
        <Field label="Estado" value={b.paymentNote} />
      </SectionCard>

      <SectionCard title="Acuerdo de vinculación y soportes adjuntos" testId="business-agreement-supports">
        <div className="mb-3 text-sm">
          {b.agreementAccepted ? (
            <span className="text-emerald-400" data-testid="business-agreement-status">
              Acuerdo aceptado por el negocio el {b.agreementAcceptedAt} (borrador v1.0).
            </span>
          ) : (
            <span className="text-gray-400" data-testid="business-agreement-status">
              El negocio aún no ha aceptado el acuerdo de vinculación.
            </span>
          )}
        </div>
        <div className="space-y-2" data-testid="business-admin-supports">
          {(b.supports || []).length === 0 && <div className="text-xs text-gray-600">Sin soportes adjuntos por el negocio.</div>}
          {(b.supports || []).map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-[#1e1e21] bg-[#101012] px-3 py-2.5" data-testid={`business-admin-support-${s.id}`}>
              <div className="min-w-[160px] grow text-sm text-gray-200">
                {s.label}
                <div className="text-xs text-gray-500">Cargado {new Date(s.uploadedAt).toLocaleDateString("es-CO")}</div>
              </div>
              <button onClick={() => setViewSupport(s)} data-testid={`business-admin-support-view-${s.id}`}
                className="flex items-center gap-1 rounded-lg bg-[#FF6A00]/15 px-2.5 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25">
                <Eye size={13} /> Ver soporte
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title={`F. Verificaciones internas (${checksDone}/${b.checks.length})`} testId="business-section-f">
        <div className="grid gap-2 sm:grid-cols-2">
          {BUSINESS_CHECKS.map((label, i) => (
            <label
              key={i}
              className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-[#1e1e21] bg-[#101012] px-3 py-2.5 text-sm text-gray-300"
              data-testid={`business-check-${i + 1}-wrapper`}
            >
              <input
                type="checkbox"
                checked={b.checks[i]}
                onChange={() => toggleCheck(i)}
                className="mt-0.5 accent-[#FF6A00]"
                data-testid={i === 0 ? "business-check-nit-checkbox" : i === 1 ? "business-check-location-checkbox" : `business-check-${i + 1}-checkbox`}
              />
              {label}
            </label>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="G. Protección de datos" testId="business-section-g">
        <Field label="Referencia de aviso / política entregada" value={b.privacyRef} />
      </SectionCard>

      <SectionCard title="H. Estado de registro y vinculación" testId="business-section-h">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Badge tone={VERIF_STATUS[b.status]} testId={`business-overall-${b.id}`}>{VERIF_STATUS[b.status].label}</Badge>
          <SemaforoBadge level={businessSemaforo(b)} testId={`business-semaforo-detail-${b.id}`} />
          {b.reviewedAt && (
            <span className="text-xs text-gray-500">Revisado por {b.reviewedBy} · {b.reviewedAt}</span>
          )}
        </div>
        <textarea
          className={`${inputCls} w-full`}
          rows={3}
          placeholder="Observaciones administrativas / motivo de rechazo (obligatorio al rechazar)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="business-reject-reason-textarea"
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={approve} data-testid="business-card-approve-button"
            className="flex items-center gap-1.5 rounded-xl bg-emerald-500/15 px-4 py-2 text-sm font-bold text-emerald-400 hover:bg-emerald-500/25">
            <Check size={15} /> Aprobar y vincular
          </button>
          <button onClick={reject} data-testid="business-card-reject-button"
            className="flex items-center gap-1.5 rounded-xl bg-red-500/10 px-4 py-2 text-sm font-bold text-red-400 hover:bg-red-500/20">
            <Ban size={15} /> Rechazar
          </button>
          <button onClick={onClose} data-testid="business-detail-close"
            className="rounded-xl border border-[#333] px-4 py-2 text-sm font-bold text-gray-400 hover:bg-[#1F1F1F]">
            Cerrar detalle
          </button>
        </div>
      </SectionCard>
      {viewSupport && (
        <ConfidentialDocModal docLabel={viewSupport.label} ownerName={b.commercialName} file={viewSupport} onClose={() => setViewSupport(null)} />
      )}
    </div>
  );
}

export default function AdminVerificationBusinesses() {
  const [params] = useSearchParams();
  const { businesses, updateBusiness } = useVerificationDemo();
  const [filter, setFilter] = useState("ALL");
  const [openId, setOpenId] = useState(params.get("sel"));

  const patch = updateBusiness;

  const list = useMemo(
    () => businesses.filter((b) => filter === "ALL" || b.status === filter),
    [businesses, filter]
  );

  return (
    <div data-testid="admin-verification-businesses">
      <PageHeader
        title="Verificación de negocios aliados"
        subtitle="Revisión de datos, verificaciones internas y aceptación del acuerdo de vinculación"
        testId="admin-businesses-tab"
        action={
          <select
            className={inputCls}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="business-status-filter-select"
          >
            <option value="ALL">Todos los estados</option>
            {Object.entries(VERIF_STATUS).filter(([k]) => k !== "EXPIRED").map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        }
      />
      <DemoBanner />
      <div className="space-y-3" data-testid="business-verification-table">
        {list.map((b) => {
          const done = b.checks.filter(Boolean).length;
          const open = openId === b.id;
          return (
            <div key={b.id} className="rounded-2xl border border-[#27272A] bg-[#141416] p-4 shadow-lg" data-testid={`business-card-${b.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-[200px] grow">
                  <div className="font-heading text-base font-bold text-white">{b.commercialName}</div>
                  <div className="text-xs text-gray-500">
                    {b.activityType} · {b.municipality} · {done}/{b.checks.length} verificaciones
                  </div>
                </div>
                <SemaforoBadge level={businessSemaforo(b)} testId={`business-semaforo-${b.id}`} />
                <Badge tone={VERIF_STATUS[b.status]} testId={`business-status-${b.id}`}>{VERIF_STATUS[b.status].label}</Badge>
                <button
                  onClick={() => setOpenId(open ? null : b.id)}
                  data-testid={`business-review-${b.id}`}
                  className="rounded-lg bg-[#FF6A00]/15 px-3 py-1.5 text-xs font-bold text-[#FF6A00] hover:bg-[#FF6A00]/25"
                >
                  {open ? "Ocultar" : "Revisar"}
                </button>
              </div>
              {open && <BusinessDetail b={b} onPatch={patch} onClose={() => setOpenId(null)} />}
            </div>
          );
        })}
        {list.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#27272A] py-10 text-center text-sm text-gray-500">
            No hay negocios con este estado
          </div>
        )}
      </div>
    </div>
  );
}
